/**
 * The «Perimetri» pages' route exports (G2 test d; ADR 6028):
 *
 *   library   one row per entry of the market's library — Scopes, store selections, product
 *             selections — its newest version, whether it is the platform's own, whether retired
 *   history   one row per version of one entry, oldest first, with who wrote and approved it and why
 *   change    one row per item of one change set: the entry, the action, the counts before and after
 *             on the masters as of today, and where it is used
 *
 * Each walks every page of its read. An empty file while the link names no market, entry or change
 * set, the filters saying which. No chart import: the manifest imports this file.
 */

import { callOperation, collectPages } from '@/api';
import {
  listLibrary, readEntry, readRunScopeChange,
  type ChangeItemEffectView, type EntryVersionView, type EntryView,
} from '@/api/generated';
import {
  buildVersion, NO_PRINCIPAL, NOT_PUBLISHED, type ExportCell, type ExportColumn, type ExportRequest, type ProvenanceFilter,
} from '@/export';

import type { ExportScope } from '../../manifest';

import { readChangePath, readEntryPath, readLibraryScope } from './scope';

export const LIBRARY_EXPORT_ID = 'inseason.runScopes.library';
export const HISTORY_EXPORT_ID = 'inseason.runScopes.history';
export const CHANGE_EXPORT_ID = 'inseason.runScopes.change';

const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value });
const num = (value: number | null | undefined): ExportCell => (value === null || value === undefined ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value });
const flag = (value: boolean): ExportCell => ({ kind: 'text', value: value ? 'true' : 'false' });

function request(meta: {
  readonly id: string;
  readonly titleKey: string;
  readonly columns: readonly ExportColumn[];
  readonly rows: readonly (readonly ExportCell[])[];
  readonly filters: readonly ProvenanceFilter[];
  readonly definition: string;
}): ExportRequest {
  return {
    id: meta.id,
    titleKey: meta.titleKey,
    columns: meta.columns,
    rows: () => meta.rows,
    rowCount: meta.rows.length,
    provenance: {
      filters: meta.filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: meta.id, definition: meta.definition, registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters: meta.filters, sort: [], visibleColumns: meta.columns.map((column) => column.key), drillPath: [], densityMode: 'compact' },
  };
}

const filter = (field: string, value: string | null): ProvenanceFilter => (value === null
  ? { field, operator: 'unset', value: '' }
  : { field, operator: 'equals', value });

/** A definition as one cell: the platform's own spelling, so the file can be read back as written. */
const definitionCell = (entry: Pick<EntryView, 'rules' | 'condition'>): ExportCell => text(
  entry.rules !== null && entry.rules !== undefined ? JSON.stringify(entry.rules)
    : entry.condition !== null && entry.condition !== undefined ? JSON.stringify(entry.condition) : null,
);

// ── library ──────────────────────────────────────────────────────────────────

const LIBRARY_DEFINITION = 'bata-reqs ADR 1003, bata-platform ADR 9033/9036: the market\'s run-scope library, each entry\'s newest version. A Scope is a set of include and exclude rules, each pairing a location side with a product side (a condition or a named selection); the cells it covers are what its include rules match less what its exclude rules match. A store or product selection is one named condition. System entries (whole country, all products, the default Scope) are the platform\'s, never edited or retired. definition is the platform\'s own spelling of the rules or the condition.';

export const LIBRARY_COLUMNS: readonly ExportColumn[] = [
  { key: 'kind', headerKey: 'inseason.runScopes.column.kind', descriptionKey: 'inseason.runScopes.columnGuide.kind', kind: 'text' },
  { key: 'entry_id', headerKey: 'inseason.runScopes.column.entryId', descriptionKey: 'inseason.runScopes.columnGuide.entryId', kind: 'text' },
  { key: 'name', headerKey: 'inseason.runScopes.column.name', descriptionKey: 'inseason.runScopes.columnGuide.name', kind: 'text' },
  { key: 'description', headerKey: 'inseason.runScopes.column.description', descriptionKey: 'inseason.runScopes.columnGuide.description', kind: 'text' },
  { key: 'version', headerKey: 'inseason.runScopes.column.version', descriptionKey: 'inseason.runScopes.columnGuide.version', kind: 'number' },
  { key: 'is_system', headerKey: 'inseason.runScopes.column.isSystem', descriptionKey: 'inseason.runScopes.columnGuide.isSystem', kind: 'text' },
  { key: 'retired', headerKey: 'inseason.runScopes.column.retired', descriptionKey: 'inseason.runScopes.columnGuide.retired', kind: 'text' },
  { key: 'recorded_at', headerKey: 'inseason.runScopes.column.recordedAt', descriptionKey: 'inseason.runScopes.columnGuide.recordedAt', kind: 'text' },
  { key: 'recorded_by', headerKey: 'inseason.runScopes.column.recordedBy', descriptionKey: 'inseason.runScopes.columnGuide.recordedBy', kind: 'text' },
  { key: 'change_id', headerKey: 'inseason.runScopes.column.changeId', descriptionKey: 'inseason.runScopes.columnGuide.changeId', kind: 'text' },
  { key: 'definition', headerKey: 'inseason.runScopes.column.definition', descriptionKey: 'inseason.runScopes.columnGuide.definition', kind: 'text' },
];

export function libraryRow(entry: EntryView): readonly ExportCell[] {
  return [
    text(entry.kind), text(entry.entryId), text(entry.label !== '' ? entry.label : entry.name), text(entry.description), num(entry.version),
    flag(entry.isSystem), flag(entry.retired), text(entry.recordedAt), text(entry.recordedBy), text(entry.changeId), definitionCell(entry),
  ];
}

export function libraryRequest(entries: readonly EntryView[], market: string | null): ExportRequest {
  return request({
    id: LIBRARY_EXPORT_ID,
    titleKey: 'inseason.runScopes.library.title',
    columns: LIBRARY_COLUMNS,
    rows: entries.map(libraryRow),
    filters: [filter('country_key', market)],
    definition: LIBRARY_DEFINITION,
  });
}

export async function libraryExport(given?: ExportScope): Promise<ExportRequest> {
  const { market, kind } = readLibraryScope(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const entries = market === null ? [] : await collectPages(listLibrary, { query: { country_key: market, include_retired: true } }, {});
  return libraryRequest((entries as readonly EntryView[]).filter((entry) => kind === null || entry.kind === kind), market);
}

// ── history ──────────────────────────────────────────────────────────────────

const HISTORY_DEFINITION = 'bata-platform ADR 9033/9035/9036: every approved version of one library entry, oldest first. A version is written only by an approved change set, with the author, the approvers and the rationale of that set; nothing is edited in place. definition is the platform\'s own spelling of the version\'s rules or condition.';

export const HISTORY_COLUMNS: readonly ExportColumn[] = [
  { key: 'version', headerKey: 'inseason.runScopes.column.version', descriptionKey: 'inseason.runScopes.columnGuide.version', kind: 'number' },
  { key: 'name', headerKey: 'inseason.runScopes.column.name', descriptionKey: 'inseason.runScopes.columnGuide.name', kind: 'text' },
  { key: 'retired', headerKey: 'inseason.runScopes.column.retired', descriptionKey: 'inseason.runScopes.columnGuide.retired', kind: 'text' },
  { key: 'change_id', headerKey: 'inseason.runScopes.column.changeId', descriptionKey: 'inseason.runScopes.columnGuide.changeId', kind: 'text' },
  { key: 'author', headerKey: 'inseason.runScopes.column.author', descriptionKey: 'inseason.runScopes.columnGuide.author', kind: 'text' },
  { key: 'approved_by', headerKey: 'inseason.runScopes.column.approvedBy', descriptionKey: 'inseason.runScopes.columnGuide.approvedBy', kind: 'text' },
  { key: 'rationale', headerKey: 'inseason.runScopes.column.rationale', descriptionKey: 'inseason.runScopes.columnGuide.rationale', kind: 'text' },
  { key: 'recorded_at', headerKey: 'inseason.runScopes.column.recordedAt', descriptionKey: 'inseason.runScopes.columnGuide.recordedAt', kind: 'text' },
  { key: 'definition', headerKey: 'inseason.runScopes.column.definition', descriptionKey: 'inseason.runScopes.columnGuide.definition', kind: 'text' },
];

export function historyRow(version: EntryVersionView): readonly ExportCell[] {
  return [
    num(version.entry.version), text(version.entry.label !== '' ? version.entry.label : version.entry.name), flag(version.entry.retired),
    text(version.entry.changeId), text(version.author), text((version.approvedBy ?? []).join('|')), text(version.rationale),
    text(version.entry.recordedAt), definitionCell(version.entry),
  ];
}

export function historyRequest(versions: readonly EntryVersionView[], market: string | null, entryId: string | null): ExportRequest {
  return request({
    id: HISTORY_EXPORT_ID,
    titleKey: 'inseason.runScopes.entry.history.title',
    columns: HISTORY_COLUMNS,
    rows: versions.map(historyRow),
    filters: [filter('country_key', market), filter('entry_id', entryId)],
    definition: HISTORY_DEFINITION,
  });
}

export async function historyExport(given?: ExportScope): Promise<ExportRequest> {
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const path = given?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const { market } = readLibraryScope(search);
  const entry = readEntryPath(path);
  const detail = market === null || entry === null
    ? null
    : await callOperation(readEntry, { path: { kind: entry.kind, entry_id: entry.entryId }, query: { country_key: market } }, {});
  return historyRequest(detail?.versions ?? [], market, entry?.entryId ?? null);
}

// ── change ───────────────────────────────────────────────────────────────────

const CHANGE_DEFINITION = 'bata-platform ADR 9035: what one change set does to the run-scope library. Per item the entry before and after, and the stores, items and cells it covers before and after, counted on the masters in force on as_of with the function a run uses; a selection\'s size, and each Scope it reaches; the Scopes that name a selection; whether production lists a Scope. "Before" and "after" are read from the item, so a pending and an approved set read the same.';

export const CHANGE_COLUMNS: readonly ExportColumn[] = [
  { key: 'kind', headerKey: 'inseason.runScopes.column.kind', descriptionKey: 'inseason.runScopes.columnGuide.kind', kind: 'text' },
  { key: 'entry_id', headerKey: 'inseason.runScopes.column.entryId', descriptionKey: 'inseason.runScopes.columnGuide.entryId', kind: 'text' },
  { key: 'action', headerKey: 'inseason.runScopes.column.action', descriptionKey: 'inseason.runScopes.columnGuide.action', kind: 'text' },
  { key: 'stores_before', headerKey: 'inseason.runScopes.column.storesBefore', descriptionKey: 'inseason.runScopes.columnGuide.storesBefore', kind: 'number' },
  { key: 'stores_after', headerKey: 'inseason.runScopes.column.storesAfter', descriptionKey: 'inseason.runScopes.columnGuide.storesAfter', kind: 'number' },
  { key: 'items_before', headerKey: 'inseason.runScopes.column.itemsBefore', descriptionKey: 'inseason.runScopes.columnGuide.itemsBefore', kind: 'number' },
  { key: 'items_after', headerKey: 'inseason.runScopes.column.itemsAfter', descriptionKey: 'inseason.runScopes.columnGuide.itemsAfter', kind: 'number' },
  { key: 'cells_before', headerKey: 'inseason.runScopes.column.cellsBefore', descriptionKey: 'inseason.runScopes.columnGuide.cellsBefore', kind: 'number' },
  { key: 'cells_after', headerKey: 'inseason.runScopes.column.cellsAfter', descriptionKey: 'inseason.runScopes.columnGuide.cellsAfter', kind: 'number' },
  { key: 'used_by_scopes', headerKey: 'inseason.runScopes.column.usedBy', descriptionKey: 'inseason.runScopes.columnGuide.usedBy', kind: 'text' },
  { key: 'in_production', headerKey: 'inseason.runScopes.column.inProduction', descriptionKey: 'inseason.runScopes.columnGuide.inProduction', kind: 'text' },
];

export function changeRow(item: ChangeItemEffectView): readonly ExportCell[] {
  return [
    text(item.kind), text(item.entryId), text(item.action),
    num(item.countsBefore?.stores), num(item.countsAfter?.stores),
    num(item.countsBefore?.items), num(item.countsAfter?.items),
    num(item.countsBefore?.cells), num(item.countsAfter?.cells),
    text((item.usedByScopes ?? []).map((scope) => scope.entryId).join('|')), flag(item.inProduction),
  ];
}

export function changeRequest(items: readonly ChangeItemEffectView[], changeId: string | null): ExportRequest {
  return request({
    id: CHANGE_EXPORT_ID,
    titleKey: 'inseason.runScopes.change.title',
    columns: CHANGE_COLUMNS,
    rows: items.map(changeRow),
    filters: [filter('change_id', changeId)],
    definition: CHANGE_DEFINITION,
  });
}

export async function changeExport(given?: ExportScope): Promise<ExportRequest> {
  const path = given?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const changeId = readChangePath(path);
  const view = changeId === null ? null : await callOperation(readRunScopeChange, { path: { change_id: changeId }, query: {} }, {});
  return changeRequest(view?.items ?? [], changeId);
}
